import React, { useContext } from "react"
import { ColorPickerStateContext } from "react-aria-components/ColorPicker"
import { SavedColorsSection } from "./saved-colors-section"
import type { SavedColors } from "./saved-colors-section"
import { useSavedColorsContext } from "./saved-colors-provider"
import { toColor, toValue } from "./use-color-value"

export type ColorPanelSavedColorsProps = {
  /** Saved colors for this panel only, instead of the `SavedColorsProvider`'s */
  savedColors?: SavedColors
}

/**
 * The account's saved colors, with "+" to save the current one. Place it after
 * `ColorPanel.Picker`; leave it out where saved colors are what's being edited.
 */
export function ColorPanelSavedColors({
  savedColors
}: ColorPanelSavedColorsProps) {
  const state = useContext(ColorPickerStateContext)
  const providedSavedColors = useSavedColorsContext()
  const saved = savedColors ?? providedSavedColors

  if (!saved && process.env.NODE_ENV !== "production") {
    throw new Error(
      "ColorPanel.SavedColors: no saved colors. Render it inside a " +
        "SavedColorsProvider, or pass `savedColors`."
    )
  }

  if (!state || !saved) return null

  return (
    <div data-slot="color-panel-saved-colors" className="flex w-full">
      <SavedColorsSection
        colors={saved.colors}
        handleColorSelect={color => {
          saved.handleColorSelect?.(color)
          state.setColor(toColor(color))
        }}
        handleColorsEdit={saved.handleColorsEdit}
        handleColorAdd={saved.handleColorAdd}
        currentColor={toValue(state.color)}
      />
    </div>
  )
}

ColorPanelSavedColors.displayName = "ColorPanel.SavedColors"
